import { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Link, useLocation } from "wouter";
import { aiServices } from "@/lib/aiServices";

type MenuKey = "company" | "services" | "ai" | "solutions";

type NavLinkItem = {
  name: string;
  href: string;
  blank?: boolean;
  desc?: string;
};

export default function Navigation() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [openMenu, setOpenMenu] = useState<MenuKey | null>(null);
  const [openMobileGroup, setOpenMobileGroup] = useState<MenuKey | null>("services");
  const dropdownRef = useRef<HTMLDivElement | null>(null);
  const [location] = useLocation();

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setOpenMenu(null);
      }
    };

    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };

    document.addEventListener("click", handleClickOutside);
    window.addEventListener("scroll", handleScroll);

    return () => {
      document.removeEventListener("click", handleClickOutside);
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  const companyLinks: NavLinkItem[] = [
    { name: "About Variable Soft", href: "/whoweare", desc: "Company, values and delivery approach" },
    { name: "Portfolio", href: "/portfolio", desc: "Client work and project examples" },
    { name: "Careers", href: "/career", desc: "Open roles and resume submission" },
    { name: "Our Team", href: "/team", desc: "People behind the delivery" },
    { name: "Blog", href: "/blog", desc: "Software, SEO and business insights" },
    { name: "India Tech Hub", href: "/india-emerging-hub", desc: "India as an emerging software hub" },
  ];

  const services: NavLinkItem[] = [
    { name: "Custom Software Development", href: "/services/software-development", desc: "Business apps, portals, APIs and dashboards" },
    { name: "Web Application Development", href: "/software-development-in-jaipur", desc: "Fast, SEO-ready websites and web apps" },
    { name: "Mobile App Development", href: "/top-software-companies-in-jaipur", desc: "Android, iOS and cross-platform apps" },
    { name: "Ecommerce Development", href: "/ecommerce-development-services", desc: "Online stores and commerce workflows" },
    { name: "UI/UX & Web Design", href: "/webdesign", desc: "Interfaces built for clarity and conversion" },
    { name: "SEO & Digital Growth", href: "/it-companies-in-jaipur", desc: "Technical SEO, content and visibility" },
    { name: "WhatsApp Business API", href: "/services/whatsapp-business-api", desc: "API setup, CRM sync and messaging automation" },
    { name: "Maintenance & Support", href: "/maintenanceandsupport", desc: "Ongoing care for websites and software" },
    { name: "Micro-GCC Teams", href: "/services/micro-gcc-india", desc: "Dedicated India software delivery teams" },
  ];

  const solutions: NavLinkItem[] = [
    { name: "CRM Software", href: "/crm", desc: "Lead, sales and customer workflows" },
    { name: "CRM Features", href: "/crm/features", desc: "Automation, reports and role-based control" },
    { name: "ERP Software", href: "/products/erp", desc: "Operations, inventory and business process flow" },
    { name: "IVR System", href: "/ivr-system", desc: "Interactive calling and routing systems" },
    { name: "Cloud Call Center", href: "/cloud-call-center", desc: "Calling workflows for support and sales" },
    { name: "Dialer Software", href: "/products/dialer", desc: "Cloud dialer and outbound calling automation" },
    { name: "WhatsApp Business API", href: "https://vscrm.in/integrations/whatsapp-business-api", blank: true, desc: "Connect WhatsApp with sales workflows" },
    { name: "Live Location Tracking", href: "https://vscrm.in/features/live-location-tracking", blank: true, desc: "Field team tracking for CRM users" },
    { name: "Task Management", href: "/products/taskmanagement", desc: "Team tasks and work visibility" },
    { name: "Hosting & SSL", href: "/products/hosting", desc: "Reliable hosting and certificate setup" },
  ];

  const dropdownVariants = {
    hidden: { opacity: 0, scale: 0.98, y: 10 },
    visible: {
      opacity: 1,
      scale: 1,
      y: 0,
      transition: { staggerChildren: 0.035 },
    },
    exit: { opacity: 0, scale: 0.98, y: 8 },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 6 },
    visible: { opacity: 1, y: 0 },
  };

  const linkClass = (href: string) =>
    `rounded-xl px-3 py-2 text-sm font-semibold transition ${
      location === href ? "text-primary" : "text-gray-800 hover:text-primary"
    }`;

  const closeMenus = () => {
    setOpenMenu(null);
    setIsMobileMenuOpen(false);
  };

  const getGroupIcon = (key: MenuKey) => {
    if (key === "company") return "fas fa-building";
    if (key === "services") return "fas fa-code";
    if (key === "ai") return "fas fa-brain";
    return "fas fa-layer-group";
  };

  const aiMenuItems: NavLinkItem[] = aiServices.map((service) => ({
    name: service.title,
    href: service.href,
    desc: service.tagline,
  }));

  const renderLink = (item: NavLinkItem, className = "") => {
    if (item.blank || item.href.startsWith("http")) {
      return (
        <a href={item.href} target="_blank" rel="noopener noreferrer" className={className}>
          {item.name}
        </a>
      );
    }

    return (
      <Link href={item.href}>
        <span onClick={closeMenus} className={className}>
          {item.name}
        </span>
      </Link>
    );
  };

  const renderDesktopDropdown = (
    key: MenuKey,
    label: string,
    eyebrow: string,
    description: string,
    items: NavLinkItem[],
    wide = false,
  ) => (
    <div className="relative">
      <button
        type="button"
        onClick={(event) => {
          event.stopPropagation();
          setOpenMenu(openMenu === key ? null : key);
        }}
        className="flex items-center gap-1 rounded-xl px-3 py-2 text-sm font-semibold text-gray-800 transition hover:text-primary"
      >
        {label}
        <motion.i
          animate={{ rotate: openMenu === key ? 180 : 0 }}
          className="fas fa-chevron-down text-[10px]"
        />
      </button>

      <AnimatePresence>
        {openMenu === key && (
          <motion.div
            variants={dropdownVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
            className={`fixed inset-x-4 top-[76px] z-50 mx-auto max-h-[calc(100vh-104px)] overflow-y-auto rounded-3xl border border-slate-200 bg-white p-3 shadow-[0_30px_100px_-55px_rgba(15,23,42,0.55)] ${
              wide ? "max-w-6xl" : "max-w-3xl"
            }`}
          >
            <div className={wide ? "grid gap-3 lg:grid-cols-[300px_1fr]" : "grid gap-3 md:grid-cols-[260px_1fr]"}>
              <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-slate-950 via-blue-950 to-slate-900 p-5 text-white">
                <div className="absolute -right-10 -top-10 h-32 w-32 rounded-full bg-cyan-400/20 blur-2xl" />
                <div className="relative">
                  <div className="grid h-12 w-12 place-items-center rounded-2xl bg-white/10 text-cyan-200 ring-1 ring-white/10">
                    <i className={getGroupIcon(key)} />
                  </div>
                  <div className="mt-5 text-xs font-black uppercase tracking-widest text-cyan-200">{eyebrow}</div>
                  <div className="mt-2 text-xl font-extrabold leading-tight">{label}</div>
                  <p className="mt-3 text-sm leading-relaxed text-white/70">{description}</p>
                  <Link href="/getintouch">
                    <span
                      onClick={closeMenus}
                      className="mt-5 inline-flex cursor-pointer items-center gap-2 rounded-xl bg-white px-4 py-2 text-sm font-bold text-slate-950 transition hover:bg-cyan-50"
                    >
                      Discuss Project
                      <i className="fas fa-arrow-right text-xs" />
                    </span>
                  </Link>
                </div>
              </div>

              <div className={`grid gap-2 ${wide ? "md:grid-cols-2 xl:grid-cols-3" : "grid-cols-1"}`}>
                {items.map((item) => (
                  <motion.div key={item.name} variants={itemVariants}>
                    {item.blank || item.href.startsWith("http") ? (
                      <a
                        href={item.href}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="group flex min-h-[86px] flex-col justify-center rounded-2xl border border-slate-100 bg-slate-50 px-4 py-3 transition hover:-translate-y-0.5 hover:border-blue-100 hover:bg-blue-50/70"
                      >
                        <span className="text-sm font-extrabold text-slate-900 group-hover:text-primary">
                          {item.name}
                        </span>
                        {item.desc && <span className="mt-1 text-xs leading-relaxed text-slate-500">{item.desc}</span>}
                      </a>
                    ) : (
                      <Link href={item.href}>
                        <span
                          onClick={closeMenus}
                          className="group flex min-h-[86px] cursor-pointer flex-col justify-center rounded-2xl border border-slate-100 bg-slate-50 px-4 py-3 transition hover:-translate-y-0.5 hover:border-blue-100 hover:bg-blue-50/70"
                        >
                          <span className="text-sm font-extrabold text-slate-900 group-hover:text-primary">
                            {item.name}
                          </span>
                          {item.desc && <span className="mt-1 text-xs leading-relaxed text-slate-500">{item.desc}</span>}
                        </span>
                      </Link>
                    )}
                  </motion.div>
                ))}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );

  return (
    <div className="force-light-mode">
      <motion.nav
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          isScrolled ? "bg-white/95 shadow-sm backdrop-blur-xl" : "bg-white"
        }`}
        initial={{ y: -100 }}
        animate={{ y: 0 }}
      >
        <div className="container mx-auto px-4 py-3 sm:px-6">
          <div className="flex items-center justify-between gap-4">
            <Link href="/">
              <motion.div whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.98 }} className="cursor-pointer">
                <img
                  src="/logo.png"
                  alt="Variable Soft Logo"
                  className="h-8 object-contain transition-all duration-300 sm:h-9 md:h-10 lg:h-12"
                />
              </motion.div>
            </Link>

            <div ref={dropdownRef} className="hidden items-center gap-1 lg:flex">
              {renderDesktopDropdown(
                "company",
                "Company",
                "WHO WE ARE",
                "Learn about Variable Soft, our work, careers and insights.",
                companyLinks,
              )}

              {renderDesktopDropdown(
                "services",
                "Services",
                "SOFTWARE DEVELOPMENT SERVICES",
                "Custom software, web, mobile, ecommerce, SEO and support services.",
                services,
                true,
              )}

              {renderDesktopDropdown(
                "ai",
                "AI Services",
                "TRENDING AI SERVICES",
                "Useful AI services for support, sales, documents, analytics and workflow automation.",
                aiMenuItems,
                true,
              )}

              {renderDesktopDropdown(
                "solutions",
                "Solutions",
                "BUSINESS SOFTWARE SOLUTIONS",
                "CRM, ERP, IVR, call center, dialer and workflow platforms.",
                solutions,
                true,
              )}

              <Link href="/services/micro-gcc-india">
                <span className={linkClass("/services/micro-gcc-india")}>Micro-GCC</span>
              </Link>

              <Link href="/blog">
                <span className={linkClass("/blog")}>Insights</span>
              </Link>

              <Link href="/getintouch">
                <Button className="ml-1 rounded-full bg-primary px-5 py-2 text-sm text-white hover:bg-primary/90">
                  Get Free Consultation
                </Button>
              </Link>
            </div>

            <button
              className="lg:hidden"
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              aria-label="Toggle navigation menu"
            >
              <motion.i
                animate={{ rotate: isMobileMenuOpen ? 90 : 0 }}
                className={`${isMobileMenuOpen ? "fas fa-times" : "fas fa-bars"} text-xl text-gray-800`}
              />
            </button>
          </div>

          <AnimatePresence>
            {isMobileMenuOpen && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: "auto", opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                className="mt-4 overflow-hidden lg:hidden"
              >
                <div className="max-h-[calc(100vh-86px)] overflow-y-auto rounded-2xl border border-gray-200 bg-white/95 p-3 text-gray-800 shadow-lg backdrop-blur-xl sm:p-4">
                  <div className="grid gap-2.5">
                    <Link href="/">
                      <div onClick={closeMenus} className="rounded-2xl bg-gradient-to-r from-blue-50 to-cyan-50 px-4 py-3 text-base font-bold text-slate-900">
                        Software Company
                      </div>
                    </Link>

                    {[
                      { key: "company" as const, title: "Company", items: companyLinks },
                      { key: "services" as const, title: "Services", items: services },
                      { key: "ai" as const, title: "AI Services", items: aiMenuItems },
                      { key: "solutions" as const, title: "Solutions", items: solutions },
                    ].map((group) => (
                      <div key={group.title} className="rounded-2xl border border-gray-200 bg-white">
                        <button
                          type="button"
                          onClick={() => setOpenMobileGroup(openMobileGroup === group.key ? null : group.key)}
                          className="flex w-full items-center justify-between px-4 py-3 text-left"
                        >
                          <span className="flex items-center gap-3 text-sm font-black uppercase tracking-widest text-primary">
                            <span className="grid h-8 w-8 place-items-center rounded-xl bg-blue-50 text-primary">
                              <i className={`${getGroupIcon(group.key)} text-xs`} />
                            </span>
                            {group.title}
                          </span>
                          <motion.i
                            animate={{ rotate: openMobileGroup === group.key ? 180 : 0 }}
                            className="fas fa-chevron-down text-xs text-slate-500"
                          />
                        </button>

                        <AnimatePresence initial={false}>
                          {openMobileGroup === group.key && (
                            <motion.div
                              initial={{ height: 0, opacity: 0 }}
                              animate={{ height: "auto", opacity: 1 }}
                              exit={{ height: 0, opacity: 0 }}
                              className="overflow-hidden"
                            >
                              <div className="grid gap-1.5 px-3 pb-3">
                                {group.items.map((item) => (
                                  <div key={item.name}>
                                    {renderLink(
                                      item,
                                      "block cursor-pointer rounded-xl bg-slate-50 px-3 py-2.5 text-sm font-semibold leading-snug text-gray-700 transition hover:bg-primary/10 hover:text-primary",
                                    )}
                                  </div>
                                ))}
                              </div>
                            </motion.div>
                          )}
                        </AnimatePresence>
                      </div>
                    ))}

                    <Link href="/getintouch">
                      <Button onClick={closeMenus} className="w-full rounded-xl bg-primary py-3 hover:bg-primary/90">
                        Get Free Consultation
                      </Button>
                    </Link>
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </motion.nav>
    </div>
  );
}
